const normalized_identity = (value = '') => {
    return `${value || ''}`.trim().toLowerCase();
};

export const read_sass_identity_aliases = (identity = {}) => {
    if (typeof identity === 'string') {
        const identityValue = normalized_identity(identity);
        return identityValue ? [identityValue] : [];
    }

    return [...new Set([
        identity?.userId,
        identity?.subId,
        identity?.sub,
        identity?.id,
        identity?._id,
        identity?.keycloakId,
        identity?.authUserId,
        identity?.email?.email,
        identity?.email,
        identity?.memberEmail,
        identity?.username,
        identity?.userName,
        identity?.memberUsername,
        identity?.preferred_username,
        identity?.optionValue,
        identity?.value
    ].map(normalized_identity).filter(Boolean))];
};

export const sass_assignee_matches_user = (assignedSeller = null, currentUser = null) => {
    const currentUserAliases = new Set(read_sass_identity_aliases(currentUser || {}));

    if (!currentUserAliases.size) {
        return false;
    }

    return read_sass_identity_aliases(assignedSeller || {}).some((identityAlias) => {
        return currentUserAliases.has(identityAlias);
    });
};

const first_string = (values = []) => {
    return values.find((value) => typeof value === 'string' && value.trim().length)?.trim() || '';
};

// Makes every accepted HR team member available for SASS self-assignment.
export const read_sass_team_member_options = (members = []) => {
    return (members || [])
        .filter((memberItem) => normalized_identity(memberItem?.accessStatus) === 'accepted')
        .map((memberItem) => {
            const memberName = first_string([
                memberItem?.name,
                memberItem?.fullName,
                `${memberItem?.firstname || memberItem?.firstName || ''} ${memberItem?.lastname || memberItem?.lastName || ''}`.trim(),
                memberItem?.username,
                memberItem?.email
            ]);
            const memberDepartment = first_string([memberItem?.department]);

            return {
                ...memberItem,
                optionValue: first_string([
                    memberItem?.email,
                    memberItem?.memberEmail,
                    memberItem?.username,
                    memberItem?.memberUsername,
                    memberItem?.userName,
                    memberItem?.preferred_username,
                    memberItem?.userId,
                    memberItem?.subId,
                    memberItem?.id,
                    memberItem?._id,
                    memberItem?.sub
                ]),
                optionLabel: memberDepartment ? `${memberName} (${memberDepartment})` : memberName
            };
        })
        .filter((memberItem) => memberItem.optionValue.length);
};

export const read_current_sass_assignee = (currentUser = {}, members = []) => {
    const matchedMember = (members || []).find((memberItem) => {
        return sass_assignee_matches_user(memberItem, currentUser);
    });
    const sourceUser = matchedMember || currentUser || {};
    const userId = first_string([
        sourceUser?.userId,
        sourceUser?.subId,
        sourceUser?.sub,
        sourceUser?.id,
        sourceUser?._id,
        currentUser?.userId,
        currentUser?.subId,
        currentUser?.sub,
        currentUser?.id,
        currentUser?._id
    ]);
    const firstname = first_string([sourceUser?.firstname, sourceUser?.firstName, currentUser?.firstname, currentUser?.firstName]);
    const lastname = first_string([sourceUser?.lastname, sourceUser?.lastName, currentUser?.lastname, currentUser?.lastName]);
    const email = first_string([
        sourceUser?.email?.email,
        sourceUser?.email,
        sourceUser?.memberEmail,
        currentUser?.email?.email,
        currentUser?.email
    ]);
    const username = first_string([
        sourceUser?.username,
        sourceUser?.userName,
        sourceUser?.memberUsername,
        sourceUser?.preferred_username,
        currentUser?.username,
        currentUser?.preferred_username
    ]);
    const name = first_string([
        sourceUser?.name,
        sourceUser?.fullName,
        `${firstname} ${lastname}`.trim(),
        currentUser?.name,
        currentUser?.fullName,
        username,
        email
    ]);

    if (!userId.length) {
        return {};
    }

    return {
        ...sourceUser,
        id: first_string([sourceUser?.id, userId]),
        userId,
        subId: first_string([sourceUser?.subId, sourceUser?.sub, userId]),
        firstname,
        lastname,
        name,
        email,
        username,
        optionValue: first_string([sourceUser?.optionValue, userId]),
        optionLabel: first_string([sourceUser?.optionLabel, name, username, email, userId])
    };
};

export const read_sass_drag_allowed = (offerId = '', boardStatus = '', assignedSeller = null, currentUser = null) => {
    const hasOffer = `${offerId || ''}`.trim().length > 0;
    const status = normalized_identity(boardStatus);

    return hasOffer && (status === 'set' || sass_assignee_matches_user(assignedSeller, currentUser));
};

export const sass_move_requires_confirmation = (
    currentStatus = '',
    nextStatus = '',
    assignedSeller = null,
    currentUser = null
) => {
    return normalized_identity(currentStatus) === 'set'
        && normalized_identity(nextStatus) === 'in_progress'
        && !sass_assignee_matches_user(assignedSeller, currentUser);
};
